import React, { useState, useEffect } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  useToast,
  InputRightElement,
  InputGroup,
  Textarea,
  Box,
  Heading,
  Divider,
  HStack,
  IconButton,
  Flex,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, AddIcon, DeleteIcon } from '@chakra-ui/icons';

const EditClubForm = ({ club, onSubmit, onCancel }) => {
  const [editedClub, setEditedClub] = useState({
    name: '',
    description: '',
    status: 'active',
    features: {
      voiceChat: false,
      chatbot: false,
    },
    voiceBots: [],
    chatBots: [],
    memberCount: 0,
    totalTokens: 0,
    email: '',
    city: '',
    country: '',
    phone: '',
    website: '',
    services: [],
    needs: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    password: '',
    confirmPassword: '',
  });

  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (club) {
      setEditedClub({
        _id: club._id,
        name: club.name || '',
        description: club.description || '',
        status: club.status || 'active',
        features: {
          voiceChat: club.features?.voiceChat || false,
          chatbot: club.features?.chatbot || false,
        },
        voiceBots: club.voiceBots || [],
        chatBots: club.chatBots || [],
        memberCount: club.memberCount || 0,
        totalTokens: club.totalTokens || 0,
        email: club.email || '',
        city: club.city || '',
        country: club.country || '',
        phone: club.phone || '',
        website: club.website || '',
        services: club.services || [],
        needs: club.needs || '',
        contactName: club.contactName || '',
        contactEmail: club.contactEmail || '',
        contactPhone: club.contactPhone || '',
        password: '',
        confirmPassword: '',
      });
    }
  }, [club]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedClub(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureChange = (feature) => {
    setEditedClub(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature]
      }
    }));
  };

  const addBot = (type) => {
    setEditedClub(prev => ({
      ...prev,
      [`${type}Bots`]: [
        ...prev[`${type}Bots`],
        {
          name: '',
          apiKey: '',
          agentId: '',
          projectId: '',
          status: 'active',
          ...(type === 'chat' ? { projectIds: [] } : {})
        }
      ]
    }));
  };

  const removeBot = (type, index) => {
    setEditedClub(prev => ({
      ...prev,
      [`${type}Bots`]: prev[`${type}Bots`].filter((_, i) => i !== index)
    }));
  };

  const updateBot = (type, index, field, value) => {
    setEditedClub(prev => ({
      ...prev,
      [`${type}Bots`]: prev[`${type}Bots`].map((bot, i) => 
        i === index ? { ...bot, [field]: value } : bot
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!club?._id) {
      toast({
        title: 'Error',
        description: 'Invalid club data. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!editedClub.email) {
      toast({
        title: 'Error',
        description: 'Email is required.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (editedClub.password || editedClub.confirmPassword) {
      if (!editedClub.password || !editedClub.confirmPassword) {
        toast({
          title: 'Error',
          description: 'Both new password fields are required if updating password.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (editedClub.password !== editedClub.confirmPassword) {
        toast({
          title: 'Error',
          description: 'New passwords do not match.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (editedClub.password.length < 6) {
        toast({
          title: 'Error',
          description: 'New password must be at least 6 characters long.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    try {
      const dataToSend = {
        ...editedClub,
        _id: club._id
      };

      if (!dataToSend.password) {
        delete dataToSend.password;
        delete dataToSend.confirmPassword;
      }

      await onSubmit(dataToSend);
    } catch (error) {
      console.error('Error in EditClubForm submit:', error);
      toast({
        title: 'Error updating club',
        description: error.response?.data?.message || error.message || 'Failed to update club',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (!club) return null;

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="md" mb={4}>Basic Information</Heading>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>user Name</FormLabel>
              <Input
                name="name"
                value={editedClub.name}
                onChange={handleInputChange}
                placeholder="Enter club name"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                name="description"
                value={editedClub.description}
                onChange={handleInputChange}
                placeholder="Enter club description"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={editedClub.email}
                onChange={handleInputChange}
                placeholder="Enter club email"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Phone</FormLabel>
              <Input
                name="phone"
                value={editedClub.phone}
                onChange={handleInputChange}
                placeholder="Enter club phone"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Website</FormLabel>
              <Input
                name="website"
                value={editedClub.website}
                onChange={handleInputChange}
                placeholder="Enter club website"
              />
            </FormControl>

            <FormControl>
              <FormLabel>City</FormLabel>
              <Input
                name="city"
                value={editedClub.city}
                onChange={handleInputChange}
                placeholder="Enter city"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Country</FormLabel>
              <Input
                name="country"
                value={editedClub.country}
                onChange={handleInputChange}
                placeholder="Enter country"
              />
            </FormControl>
          </VStack>
        </Box>

        <Divider />

        <Box>
          <Heading size="md" mb={4}>Contact Information</Heading>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Contact Name</FormLabel>
              <Input
                name="contactName"
                value={editedClub.contactName}
                onChange={handleInputChange}
                placeholder="Enter contact name"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Contact Email</FormLabel>
              <Input
                name="contactEmail"
                type="email"
                value={editedClub.contactEmail}
                onChange={handleInputChange}
                placeholder="Enter contact email"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Contact Phone</FormLabel>
              <Input
                name="contactPhone"
                value={editedClub.contactPhone}
                onChange={handleInputChange}
                placeholder="Enter contact phone"
              />
            </FormControl>
          </VStack>
        </Box>

        <Divider />

        <Box>
          <Heading size="md" mb={4}>Account Settings</Heading>
          <VStack spacing={4}>
            {club.userEmail && (
              <FormControl>
                <FormLabel>User Email</FormLabel>
                <Input value={club.userEmail} isReadOnly />
              </FormControl>
            )}
            {club.userPassword && (
              <FormControl>
                <FormLabel>Original Password</FormLabel>
                <InputGroup>
                  <Input
                    value={club.userPassword}
                    isReadOnly
                    type={showPassword ? 'text' : 'password'}
                  />
                  <InputRightElement width="4.5rem">
                    <Button h="1.75rem" size="sm" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </FormControl>
            )}
            <FormControl>
              <FormLabel>New Password (leave blank to keep current)</FormLabel>
              <InputGroup>
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={editedClub.password}
                  onChange={handleInputChange}
                  placeholder="Enter new password"
                />
                <InputRightElement width="4.5rem">
                  <Button h="1.75rem" size="sm" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel>Confirm New Password</FormLabel>
              <InputGroup>
                <Input
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={editedClub.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm new password"
                />
                <InputRightElement width="4.5rem">
                  <Button h="1.75rem" size="sm" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel>Status</FormLabel>
              <Select
                name="status"
                value={editedClub.status}
                onChange={handleInputChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </Select>
            </FormControl>
          </VStack>
        </Box>

        <Divider />

        <Box>
          <Heading size="md" mb={4}>Features & Services</Heading>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Features</FormLabel>
              <VStack align="start" spacing={2}>
                <Checkbox
                  isChecked={editedClub.features.voiceChat}
                  onChange={() => handleFeatureChange('voiceChat')}
                >
                  Voice Chat
                </Checkbox>
                <Checkbox
                  isChecked={editedClub.features.chatbot}
                  onChange={() => handleFeatureChange('chatbot')}
                >
                  Chatbot
                </Checkbox>
              </VStack>
            </FormControl>

            <FormControl>
              <FormLabel>Services (comma-separated)</FormLabel>
              <Input
                name="services"
                value={Array.isArray(editedClub.services) ? editedClub.services.join(', ') : editedClub.services}
                onChange={(e) => setEditedClub(prev => ({
                  ...prev,
                  services: e.target.value.split(',').map(service => service.trim())
                }))}
                placeholder="Enter services (e.g., ticket booking, customer support)"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Club Needs</FormLabel>
              <Textarea
                name="needs"
                value={editedClub.needs}
                onChange={handleInputChange}
                placeholder="Describe your club's specific needs"
              />
            </FormControl>
          </VStack>
        </Box>

        {editedClub.features.voiceChat && (
          <>
            <Divider />
            <Box>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="md">Voice Bots</Heading>
                <Button leftIcon={<AddIcon />} size="sm" onClick={() => addBot('voice')}>
                  Add Voice Bot
                </Button>
              </Flex>
              <VStack spacing={4}>
                {editedClub.voiceBots.map((bot, index) => (
                  <Box key={index} p={4} borderWidth={1} borderRadius="md" width="100%">
                    <Flex justify="space-between" mb={2}>
                      <Heading size="sm">Voice Bot {index + 1}</Heading>
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => removeBot('voice', index)}
                      />
                    </Flex>
                    <VStack spacing={3}>
                      <FormControl>
                        <FormLabel>Bot Name</FormLabel>
                        <Input
                          value={bot.name}
                          onChange={(e) => updateBot('voice', index, 'name', e.target.value)}
                          placeholder="Enter bot name"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>API Key</FormLabel>
                        <Input
                          value={bot.apiKey}
                          onChange={(e) => updateBot('voice', index, 'apiKey', e.target.value)}
                          placeholder="Enter API Key"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Agent ID</FormLabel>
                        <Input
                          value={bot.agentId}
                          onChange={(e) => updateBot('voice', index, 'agentId', e.target.value)}
                          placeholder="Enter Agent ID"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Project ID</FormLabel>
                        <Input
                          value={bot.projectId}
                          onChange={(e) => updateBot('voice', index, 'projectId', e.target.value)}
                          placeholder="Enter Project ID"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Status</FormLabel>
                        <Select
                          value={bot.status}
                          onChange={(e) => updateBot('voice', index, 'status', e.target.value)}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Select>
                      </FormControl>
                    </VStack>
                  </Box>
                ))}
              </VStack>
            </Box>
          </>
        )}

        {editedClub.features.chatbot && (
          <>
            <Divider />
            <Box>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="md">Chat Bots</Heading>
                <Button leftIcon={<AddIcon />} size="sm" onClick={() => addBot('chat')}>
                  Add Chat Bot
                </Button>
              </Flex>
              <VStack spacing={4}>
                {editedClub.chatBots.map((bot, index) => (
                  <Box key={index} p={4} borderWidth={1} borderRadius="md" width="100%">
                    <Flex justify="space-between" mb={2}>
                      <Heading size="sm">Chat Bot {index + 1}</Heading>
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => removeBot('chat', index)}
                      />
                    </Flex>
                    <VStack spacing={3}>
                      <FormControl>
                        <FormLabel>Bot Name</FormLabel>
                        <Input
                          value={bot.name}
                          onChange={(e) => updateBot('chat', index, 'name', e.target.value)}
                          placeholder="Enter bot name"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>API Key</FormLabel>
                        <Input
                          value={bot.apiKey}
                          onChange={(e) => updateBot('chat', index, 'apiKey', e.target.value)}
                          placeholder="Enter API Key"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Agent ID</FormLabel>
                        <Input
                          value={bot.agentId}
                          onChange={(e) => updateBot('chat', index, 'agentId', e.target.value)}
                          placeholder="Enter Agent ID"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Project IDs (comma-separated)</FormLabel>
                        <Input
                          value={Array.isArray(bot.projectIds) ? bot.projectIds.join(', ') : bot.projectIds}
                          onChange={(e) => updateBot('chat', index, 'projectIds', e.target.value.split(',').map(id => id.trim()))}
                          placeholder="Enter Project IDs"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Status</FormLabel>
                        <Select
                          value={bot.status}
                          onChange={(e) => updateBot('chat', index, 'status', e.target.value)}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Select>
                      </FormControl>
                    </VStack>
                  </Box>
                ))}
              </VStack>
            </Box>
          </>
        )}

        <Divider />

        <Box>
          <Heading size="md" mb={4}>Usage Statistics</Heading>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Member Count</FormLabel>
              <NumberInput
                name="memberCount"
                value={editedClub.memberCount}
                onChange={(valueString) => setEditedClub(prev => ({ ...prev, memberCount: parseInt(valueString) }))}
                min={0}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl>
              <FormLabel>Total Tokens</FormLabel>
              <NumberInput
                name="totalTokens"
                value={editedClub.totalTokens}
                onChange={(valueString) => setEditedClub(prev => ({ ...prev, totalTokens: parseInt(valueString) }))}
                min={0}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </VStack>
        </Box>

        <HStack spacing={4} justify="flex-end">
          <Button onClick={onCancel}>Cancel</Button>
          <Button colorScheme="teal" type="submit">
            Save Changes
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};

export default EditClubForm; 